import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BlogLayout from './BlogLayout';
import { useBlogLang } from '../hooks/useBlogLang';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://lempole.com';

const COPY = {
  it: {
    kicker: 'informativa legale',
    title: 'Termini e Condizioni',
    intro: 'Benvenuti sul sito Lempole.com. Continuando a navigare accettate i presenti Termini e Condizioni. La consultazione del sito è riservata a persone maggiorenni.',
    sections: [
      {
        h: '1. Titolare del sito',
        p: 'Il presente sito è gestito da Madonnino dei Campacci di Tiezzi Raffaello, con sede in Via Lamole 53, 50022 Greve in Chianti (FI), Italia. Partita IVA 6836710480. Per qualsiasi comunicazione: madonninocampacci@gmail.com — +39 328 9462581.',
      },
      {
        h: '2. Natura del sito',
        p: 'Lempole.com è un sito editoriale e di presentazione del progetto vitivinicolo Lempole e dell\u2019azienda agricola Madonnino dei Campacci. Il sito non costituisce un negozio online: nessuna transazione o vendita di prodotti viene conclusa direttamente attraverso queste pagine. Eventuali richieste di acquisto sono gestite tramite contatto diretto (email o WhatsApp) e sono riservate a maggiorenni. La vendita di bevande alcoliche a minori di 18 anni è vietata dalla legge italiana.',
      },
      {
        h: '3. Consumo responsabile',
        p: 'Il vino è un prodotto agricolo che invita alla lentezza e alla convivialità. Vi invitiamo a consumarlo con moderazione. Non guidate se avete bevuto. La salute è più importante di qualsiasi calice.',
      },
      {
        h: '4. Proprietà intellettuale',
        p: 'Tutti i contenuti del sito \u2014 testi, immagini, illustrazioni, marchi, layout, codice \u2014 sono di proprietà esclusiva di Madonnino dei Campacci o dei rispettivi autori, e sono protetti dalle leggi italiane e internazionali sul diritto d\u2019autore e sui marchi. È vietata la riproduzione, anche parziale, senza autorizzazione scritta. Per richieste editoriali o giornalistiche potete scriverci a madonninocampacci@gmail.com.',
      },
      {
        h: '5. Accuratezza dei contenuti',
        p: 'Facciamo del nostro meglio per pubblicare informazioni accurate e aggiornate. Tuttavia, dati come annate, disponibilità, caratteristiche organolettiche possono variare nel tempo. Non forniamo garanzie di completezza o assenza di errori. In caso di discrepanza rispetto a comunicazioni scritte private, fanno fede queste ultime.',
      },
      {
        h: '6. Link a siti di terze parti',
        p: 'Il sito può contenere link verso servizi esterni (Instagram, WhatsApp, Iubenda, LinkedIn, mappe, ecc.). Non siamo responsabili dei contenuti e delle politiche di questi terzi. Vi invitiamo a leggere autonomamente le loro condizioni d\u2019uso.',
      },
      {
        h: '7. Cookie e dati personali',
        p: 'L\u2019utilizzo dei cookie e il trattamento dei dati personali sono regolati da apposite informative dedicate, disponibili in fondo a questa pagina: Privacy Policy e Cookie Policy (gestite tramite Iubenda). Potete gestire o revocare il consenso in qualsiasi momento tramite la finestra dedicata.',
      },
      {
        h: '8. Modifiche',
        p: 'Possiamo aggiornare i presenti Termini in qualunque momento, per adeguarli alla legge o al progetto. La versione in vigore è quella pubblicata su questa pagina; l\u2019ultima data di aggiornamento è indicata in fondo.',
      },
      {
        h: '9. Legge applicabile e foro',
        p: 'I presenti Termini sono regolati dalla legge italiana. Per ogni controversia sarà competente in via esclusiva il Foro di Firenze, salvo il foro inderogabile del consumatore.',
      },
    ],
    updated: 'Ultimo aggiornamento: febbraio 2026.',
    back: 'Torna al sito',
    seoTitle: 'Termini e Condizioni — Lempole',
    seoDesc: 'Termini e Condizioni d\u2019uso del sito lempole.com, gestito da Madonnino dei Campacci di Tiezzi Raffaello.',
  },
  en: {
    kicker: 'legal notice',
    title: 'Terms and Conditions',
    intro: 'Welcome to Lempole.com. By continuing to browse this website you accept these Terms and Conditions. Access is reserved to adults (18+).',
    sections: [
      {
        h: '1. Site owner',
        p: 'This website is operated by Madonnino dei Campacci di Tiezzi Raffaello, based at Via Lamole 53, 50022 Greve in Chianti (FI), Italy. VAT 6836710480. Contact: madonninocampacci@gmail.com — +39 328 9462581.',
      },
      {
        h: '2. Nature of the site',
        p: 'Lempole.com is an editorial and presentation website for the Lempole wine project and the Madonnino dei Campacci farm. It is not an online shop: no transaction or sale is concluded directly through these pages. Purchase requests are handled via direct contact (email or WhatsApp) and are reserved to adults. Selling alcoholic beverages to minors is forbidden by Italian law.',
      },
      {
        h: '3. Responsible drinking',
        p: 'Wine is an agricultural product that invites slowness and conviviality. Please enjoy it in moderation. Do not drink and drive. Health is more important than any glass.',
      },
      {
        h: '4. Intellectual property',
        p: 'All contents on this site \u2014 texts, images, illustrations, trademarks, layout, code \u2014 are the exclusive property of Madonnino dei Campacci or the respective authors, and are protected by Italian and international copyright and trademark laws. Reproduction, even partial, without written permission is forbidden. For editorial or press requests please email madonninocampacci@gmail.com.',
      },
      {
        h: '5. Accuracy of contents',
        p: 'We strive to publish accurate and up-to-date information. However, data such as vintages, availability and tasting notes may vary over time. We do not guarantee completeness or absence of errors. In case of discrepancy with private written communications, the latter prevail.',
      },
      {
        h: '6. Third-party links',
        p: 'This website may include links to third-party services (Instagram, WhatsApp, Iubenda, LinkedIn, maps, etc.). We are not responsible for their content or policies. Please read their terms of use separately.',
      },
      {
        h: '7. Cookies and personal data',
        p: 'The use of cookies and the processing of personal data are governed by dedicated notices available at the bottom of this page: Privacy Policy and Cookie Policy (managed via Iubenda). You may withdraw or update your consent at any time via the dedicated window.',
      },
      {
        h: '8. Changes',
        p: 'We may update these Terms at any time to reflect law updates or changes in the project. The version in force is the one published on this page; the last update date is indicated below.',
      },
      {
        h: '9. Governing law and jurisdiction',
        p: 'These Terms are governed by Italian law. For any dispute the court of Florence shall have exclusive jurisdiction, subject to the mandatory consumer forum.',
      },
    ],
    updated: 'Last update: February 2026.',
    back: 'Back to site',
    seoTitle: 'Terms and Conditions — Lempole',
    seoDesc: 'Terms and Conditions for the lempole.com website, operated by Madonnino dei Campacci di Tiezzi Raffaello.',
  },
  fr: {
    kicker: 'mentions légales',
    title: 'Conditions Générales',
    intro: 'Bienvenue sur Lempole.com. En continuant à naviguer sur ce site, vous acceptez les présentes Conditions Générales. L\u2019accès est réservé aux personnes majeures.',
    sections: [
      {
        h: '1. Éditeur du site',
        p: 'Ce site est édité par Madonnino dei Campacci di Tiezzi Raffaello, siège à Via Lamole 53, 50022 Greve in Chianti (FI), Italie. TVA 6836710480. Contact : madonninocampacci@gmail.com — +39 328 9462581.',
      },
      {
        h: '2. Nature du site',
        p: 'Lempole.com est un site éditorial et de présentation du projet vinicole Lempole et de l\u2019exploitation agricole Madonnino dei Campacci. Il ne s\u2019agit pas d\u2019une boutique en ligne : aucune transaction n\u2019est conclue directement par ces pages. Les demandes d\u2019achat sont traitées via contact direct (email ou WhatsApp) et sont réservées aux personnes majeures. La vente d\u2019alcool aux mineurs est interdite par la loi italienne.',
      },
      {
        h: '3. Consommation responsable',
        p: 'Le vin est un produit agricole qui invite à la lenteur et à la convivialité. Nous vous invitons à le consommer avec modération. L\u2019abus d\u2019alcool est dangereux pour la santé. Ne conduisez pas si vous avez bu.',
      },
      {
        h: '4. Propriété intellectuelle',
        p: 'Tous les contenus du site \u2014 textes, images, illustrations, marques, mise en page, code \u2014 sont la propriété exclusive de Madonnino dei Campacci ou des auteurs respectifs, et sont protégés par les lois italiennes et internationales sur le droit d\u2019auteur et les marques. Toute reproduction, même partielle, sans autorisation écrite est interdite. Pour toute demande éditoriale ou de presse : madonninocampacci@gmail.com.',
      },
      {
        h: '5. Exactitude des contenus',
        p: 'Nous nous efforçons de publier des informations exactes et à jour. Cependant, des données comme les millésimes, la disponibilité et les notes de dégustation peuvent varier avec le temps. Nous ne garantissons pas l\u2019exhaustivité ou l\u2019absence d\u2019erreurs. En cas de divergence avec des communications privées écrites, ces dernières prévalent.',
      },
      {
        h: '6. Liens vers des tiers',
        p: 'Le site peut contenir des liens vers des services tiers (Instagram, WhatsApp, Iubenda, LinkedIn, cartes, etc.). Nous ne sommes pas responsables de leurs contenus et politiques. Merci de lire séparément leurs conditions d\u2019utilisation.',
      },
      {
        h: '7. Cookies et données personnelles',
        p: 'L\u2019utilisation des cookies et le traitement des données personnelles sont régis par des mentions dédiées, accessibles en bas de cette page : Privacy Policy et Cookie Policy (gérées via Iubenda). Vous pouvez retirer ou modifier votre consentement à tout moment via la fenêtre dédiée.',
      },
      {
        h: '8. Modifications',
        p: 'Nous pouvons mettre à jour les présentes Conditions à tout moment, pour tenir compte de la loi ou de l\u2019évolution du projet. La version en vigueur est celle publiée sur cette page ; la date de dernière mise à jour est indiquée ci-dessous.',
      },
      {
        h: '9. Loi applicable et juridiction',
        p: 'Les présentes Conditions sont soumises au droit italien. Tout litige relève de la compétence exclusive du tribunal de Florence, sauf for consommateur impératif.',
      },
    ],
    updated: 'Dernière mise à jour : février 2026.',
    back: 'Retour au site',
    seoTitle: 'Conditions Générales — Lempole',
    seoDesc: 'Conditions Générales du site lempole.com, édité par Madonnino dei Campacci di Tiezzi Raffaello.',
  },
};

export default function TermsPage() {
  const { lang } = useBlogLang();
  const c = COPY[lang] || COPY.it;
  const suffix = lang === 'it' ? '' : `?lang=${lang}`;

  useSeo({
    title: c.seoTitle,
    description: c.seoDesc,
    canonical: `${SITE}/termini${suffix}`,
    type: 'article',
    keywords: 'termini, condizioni, lempole, madonnino dei campacci',
    jsonLd: null,
  });

  return (
    <BlogLayout>
      <div className="terms-page">
        <header className="terms-head">
          <Link to={`/${suffix}`} className="terms-crumb font-kicker" data-testid="terms-back">
            <ArrowLeft size={14} /> {c.back}
          </Link>
        </header>

        <article className="terms-body">
          <span className="eyebrow">{c.kicker}</span>
          <h1 className="terms-title font-display">{c.title}</h1>
          <p className="terms-intro font-display-italic">{c.intro}</p>

          {c.sections.map((s, i) => (
            <section key={i} className="terms-section">
              <h2 className="terms-h font-display">{s.h}</h2>
              <p>{s.p}</p>
            </section>
          ))}

          <p className="terms-updated font-kicker">{c.updated}</p>
        </article>
      </div>
    </BlogLayout>
  );
}
